<template>
  <div>
    <div class="confirmation-container hide-for-print">
      <MedalliaSurvey page="return-confirmation" />
      <h1 id="return-confirmation" class="margin-top-xs" :class="showAlertMessage ? 'padding-bottom-xxs' : 'padding-bottom-s'">
        Return Confirmation
      </h1>

      <div v-if="showAlertMessage">
        <Notification :severity="returnDetails?.orderReturnAlertMessage.web?.message_type?.toLowerCase()" :closable="false">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="sanitizedAlertMessage"></div>
        </Notification>
      </div>

      <div v-if="returnConfirmationStore.returnLabel.showNotification" class="return-label-notification padding-bottom-xxs">
        <Notification severity="success" :closable="false" aria-live="assertive">
          We emailed your return confirmation to
          {{ returnConfirmationStore.returnLabel.emailAddress }}.
        </Notification>
      </div>
      <template v-if="returnDetails">
        <div id="resend-email-section" class="border-bottom padding-bottom-s margin-bottom-s">
          <p
            v-if="!returnConfirmationStore.returnLabel.showNotification"
            id="resend-email-notification"
            class="margin-bottom-xs"
            aria-live="assertive"
            aria-labelledby="return-confirmation"
            v-text="returnConfirmationText"
          ></p>
          <button v-if="!showResendEmailForm" class="link-med" @click="showResendEmailForm = true">Send to another email</button>
          <ResendEmailForm v-if="showResendEmailForm" />
        </div>

        <div class="grid-x return-details-instructions-grid">
          <div v-if="showQrCode" class="cell small-12 medium-8">
            <QRCode :qrcode-number="getQRcodeNumber()" :scan-text="getScanText()" :hide-number="hideQRCodeNumber" is-text-left priority="high" />
          </div>

          <div
            v-if="returnDetails?.returnDropOffMethod === 'UPS' || returnDetails?.returnDropOffMethod === 'FEDEX'"
            class="cell small-12 medium-7 margin-bottom-m"
          >
            <button
              id="print-label"
              class="primary button expanded padding-top-xs padding-bottom-xs padding-left-s padding-right-s"
              @click.prevent="printLabel"
            >
              Print Return Label
            </button>
          </div>

          <div class="cell small-12 medium-12">
            <ReturnInstructions
              uop-flow
              :return-drop-off-method="returnDetails?.returnDropOffMethod"
              :refund-method="returnDetails?.refundMethod"
              :is-mkp-order="isMarketplaceOrder"
              :items="returnItems"
            />
            <button id="locator-link" class="link-med margin-top-xs" @click="showLocatorSlideout" v-text="locatorLinkText"></button>
          </div>
        </div>

        <div class="return-details padding-bottom-s margin-bottom-m border-bottom">
          <ReturnSummary :return-details="returnDetails" page="return-confirmation" />
        </div>

        <div class="refund-faq padding-bottom-m">
          <template v-if="showRefundInfoSection">
            <div class="h4">When will I get my refund?</div>
            <RefundInfo :return-details="returnDetails" aria-live="off" />
          </template>
          <div class="grid-x margin-top-m">
            <div class="cell small-12 medium-7">
              <a
                id="continue-shopping"
                href="/"
                class="primary button expanded padding-top-xs padding-bottom-xs padding-left-s padding-right-s"
                role="button"
              >
                Continue Shopping
              </a>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="showPrintLabelComponent && (returnDetails?.returnDropOffMethod === 'UPS' || returnDetails?.returnDropOffMethod === 'FEDEX')"
      class="print-return-label-confirmation-container medium-12 small-12"
    >
      <PrintReturnLabel :return-details="returnDetails" />
    </div>

    <Skeleton v-if="!returnDetails" />
  </div>
</template>

<script setup>
// Imports
import { defineAsyncComponent, markRaw, computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { sanitize } from '@vue-util/sanitize';
import { fireTag } from '@vue-util/tagManager';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';

// Local Import
import ReturnInstructions from '@/components/returns/ReturnInstructions.vue';
import RefundInfo from '@/components/returns/RefundInfo.vue';
import ReturnSummary from '@/components/returns/ReturnSummary.vue';
import QRCode from '@/components/common/QRCode.vue';
import PrintReturnLabel from '@/components/returns/PrintReturnLabel.vue';
import MedalliaSurvey from '@/components/MedalliaSurvey.vue';
import ResendEmailForm from '@/components/returns/ResendEmailForm.vue';
import Skeleton from '@/components/skeleton/ReturnConfirmation.vue';

//Utility Functions and Constants
import { getTitle } from '@/utils/returns/ReturnInstruction';
import { NOTIFICATION_TYPES, RETURN_METHOD_MAP, LOCATOR_LOCATION_RADIUS, LOCATOR_RESULT_LIMIT } from '#shared/constants';

// Config
import { brand, RETURN_METHOD_KEY_MAP, LOCATOR_LINK_TEXT } from 'config';

// Stores and Composable
import { useReturnConfirmationStore } from '@/stores/returns/useReturnConfirmationStore';

const returnConfirmationStore = useReturnConfirmationStore();
const envPropsStore = useEnvPropsStore();
const { registerSlideout, toggleSlideout } = useSlideout();
const showResendEmailForm = ref(false);
const showPrintLabelComponent = ref(false);

const returnDetails = computed(() => returnConfirmationStore.returnDetails?.returnDetails);

const isMarketplaceOrder = computed(() => returnDetails.value?.isMkpOrder);

const isFedExReturnMethod = computed(() => brand === 'bcom' && returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.fedex);

const returnConfirmationText = computed(() => {
  const isUPSReturn = returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups;

  if (isUPSReturn || isFedExReturnMethod.value) {
    return `We emailed your return confirmation to ${returnDetails.value?.emailAddress}.`;
  }
  return `We emailed your return confirmation and QR code to ${returnDetails.value?.emailAddress}.`;
});

const showAlertMessage = computed(() => {
  const web = returnDetails.value?.orderReturnAlertMessage?.web || {};
  return web.message_type && web.message && NOTIFICATION_TYPES.includes(web.message_type?.toLowerCase());
});

const sanitizedAlertMessage = computed(() => sanitize(returnDetails.value?.orderReturnAlertMessage?.web?.message));

const returnItems = computed(() => {
  const items = returnDetails.value?.items || [];
  return items.map((item) => ({
    ...item,
    hazmatDescription: item.itemDetails?.hazmatDescription,
  }));
});

const locatorLinkText = computed(() => LOCATOR_LINK_TEXT.config()[returnDetails.value?.returnDropOffMethod]);

const showQrCode = computed(() =>
  [RETURN_METHOD_KEY_MAP.labelFree, RETURN_METHOD_KEY_MAP.storeReturn].includes(returnDetails.value?.returnDropOffMethod),
);

const hideQRCodeNumber = computed(() => returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn);

const showRefundInfoSection = computed(
  () =>
    returnDetails.value?.returnDropOffMethod !== RETURN_METHOD_KEY_MAP.storeReturn &&
    returnDetails.value?.items.some((item) => item.returnType === 'PR'),
);

onMounted(() => {
  setTimeout(() => {
    medalliaSurveyConfig();
    fireTealiumTagOnPageLoad();
  }, 300);
});

onUnmounted(() => {
  returnConfirmationStore.CLEAR_RETURN_CONFIRMATION();
});

async function showLocatorSlideout(event) {
  const {
    addressLine1 = '',
    addressLine2 = '',
    addressLine3 = '',
    city = '',
    state = '',
    postalCode = '',
  } = {
    ...(returnDetails.value?.contact?.address || {}),
  };
  const address = [addressLine1, addressLine2, addressLine3, city, state, postalCode].filter((i) => i).join(', ');
  registerSlideout({
    id: 'return-confirmation-slideout',
    title: getTitle(returnDetails.value?.returnDropOffMethod),
    elementIdToFocus: event?.target.id,
    props: {
      typeOf: returnDetails.value?.returnDropOffMethod,
      address,
      useMspLocatorApi: true,
      newGoogleApiCall: returnDetails.value?.isNewGoogleAPICallEnabled,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
      storeInPreferenceDatabaseEnabled: returnDetails.value?.storeInPreferenceDatabaseEnabled,
      newPlacesDetailsApiEnabled: returnDetails.value?.newPlacesDetailsApiEnabled,
      commonLocationServiceApiKey: envPropsStore.envProps.xMacysApiKey,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });
  toggleSlideout('return-confirmation-slideout');
}

async function printLabel() {
  showPrintLabelComponent.value = true;
  await nextTick();
  window.print();
  const tagData = {
    link_name: 'print return label',
    event_name: 'print return label',
  };
  fireTag('link', tagData);
}

function fireTealiumTagOnPageLoad() {
  const totalRefundAmount = returnDetails.value?.estimatedRefund;
  const isLabelFreeReturn = returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree ? 'true' : 'false';
  const refundType = returnDetails.value?.refundMethod || '';
  const returnMethod = returnDetails.value?.returnDropOffMethod;
  const productIds = [];
  const returnedQuantities = [];
  const reasonsForReturn = [];
  const currentPrice = [];
  const protectionPlanPurchased = [];
  const protectionPlanReturnAmount = [];

  returnDetails.value?.items.forEach((item) => {
    productIds.push(item.itemDetails?.productId || '');
    returnedQuantities.push(item.returnQuantity || 0);
    reasonsForReturn.push(item.reasonDescription || '');
    currentPrice.push(item.currentPrice?.toString()?.replace(/\$/g, '') * item.returnQuantity?.toString() || '');
    protectionPlanPurchased.push(item.itemDetails?.planDesc || '');
    protectionPlanReturnAmount.push(item.itemDetails?.protectionPlanPrice?.toString()?.replace(/\$/g, '') * item.returnQuantity?.toString() || '');
  });

  fireTag('view', {
    page_name: 'return confirmation',
    page_type: 'order history',
    site_action: 'submit returns',
    action_location: 'order details',
    product_id: productIds,
    quantity_returned: returnedQuantities,
    reason_for_return: reasonsForReturn,
    return_product_prices: currentPrice,
    return_refund_amount: totalRefundAmount,
    protection_plan_purchased: protectionPlanPurchased,
    protection_plan_return_amount: protectionPlanReturnAmount,
    return_method: returnMethod,
    label_free_return: isLabelFreeReturn,
    refund_type: refundType,
    marketPlaceItem: isMarketplaceOrder.value ? 'true' : 'false',
  });
}

function medalliaSurveyConfig() {
  const MEDALLIA_SURVEY = window.KAMPYLE_ONSITE_SDK;
  if (!MEDALLIA_SURVEY || typeof MEDALLIA_SURVEY.updatePageView !== 'function') return;
  global.pathwayForMedallia = RETURN_METHOD_MAP[returnDetails.value?.returnDropOffMethod];
  global.marketPlaceItem = isMarketplaceOrder.value ? 'true' : 'false';
  global.beneficiaryUserState = returnDetails.value?.beneficiaryUserState;
  MEDALLIA_SURVEY.updatePageView();
}

function getQRcodeNumber() {
  if (returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree)
    return returnDetails.value?.labelFreeReturnTrackingNumber?.toString();
  if (returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn) return returnDetails.value?.pendingReturnId?.toString();
  return '';
}

function getScanText() {
  if (returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree) return 'Scan in The UPS Store';
  if (returnDetails.value?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn) return 'Scan in store';
  return '';
}
</script>
<style lang="scss">
.print-return-label-confirmation-container {
  display: none;
}
@media print {
  #nav-header-wrapper,
  #nav-header,
  #nav-footer {
    display: none;
  }

  section .banner-content {
    display: none !important;
  }

  .print-return-label-confirmation-container {
    display: block;
    position: relative;
    overflow-y: visible;
  }
}
</style>
